import {
  LitElement,
  html,
  css,
  unsafeHTML,
  utils,
  paths,
  t,
  broadcastReceiver,
} from '@hh/global.js'

class HHModal extends LitElement {
  static properties = {
    id: {},
    open: {
      reflect: true,
    },
  }

  constructor() {
    super()

    this.closeFromBackdrop = this.closeFromBackdrop.bind(this)
  }

  requestUpdate(name, oldValue) {
    if (name === 'open') {
      const isOpen = !!this[name] && this[name] !== 'false'

      if (isOpen) {
        utils.pauseAllMedia()
        this.trigger = document.activeElement
      } else if (this.trigger) {
        this.trigger.focus?.()
        this.trigger = null
      }
    }

    return super.requestUpdate(name, oldValue)
  }

  connectedCallback() {
    super.connectedCallback()

    this.receiver = broadcastReceiver('modal', (...args) => {
      this.openFromDocument(...args)
    })

    this.addEventListener('click', this.closeFromBackdrop)
  }

  disconnectedCallback() {
    super.disconnectedCallback()

    this.removeEventListener('click', this.closeFromBackdrop)
    this.receiver?.disconnect()
  }

  closeFromBackdrop(event) {
    if (event.target === this) {
      this.open = false
    }
  }

  openFromDocument(data) {
    if (this.id === data.id) {
      this.open = true
    } else {
      this.open = false
      return
    }

    if (data.element) {
      setTimeout(() => {
        this.querySelector(`[id="${data.element}"]`)?.scrollIntoView()
      }, 0)
    }
  }

  close(event) {
    if (event.type === 'click') {
      this.open = false
      return
    }
    event.code.toUpperCase() === 'ESCAPE' && (this.open = false)
  }

  render() {
    return html`<div
      @keyup="${this.close}"
      role="dialog"
      aria-modal="true"
      tabindex="-1"
    >
      <button
        @click="${this.close}"
        class="button"
        type="button"
        aria-label="${t('accessibility.close')}"
      >
        ${unsafeHTML(paths.close)}
      </button>
      <div
        class="content"
        role="document"
        aria-label="${t('products.modal.label')}"
        tabindex="0"
      >
        <hh-focus-trap active="${this.open}">
          <slot></slot>
        </hh-focus-trap>
      </div>
    </div>`
  }

  static styles = css`
    :host {
      background-color: var(--hh-background);
      box-sizing: border-box;
      height: 100%;
      left: 0;
      opacity: 0;
      position: fixed;
      top: 0;
      width: 100%;
      visibility: hidden;
      z-index: -1;
    }

    :host([size='flyout']) {
      max-width: var(--hh-max-width, 400px);
      height: auto;
      top: var(--hh-flyout-offset-top, 3rem);
      right: var(--hh-flyout-offset-right, 3rem);
      left: revert;
      border: 0.1rem solid var(--hh-border-color);
    }

    :host([size='popup']) {
      background-color: var(--hh-overlay-tint, rgba(0, 0, 0, 0.55));
      -webkit-backdrop-filter: blur(var(--hh-overlay-blur, 2.5px));
      backdrop-filter: blur(var(--hh-overlay-blur, 2.5px));
    }

    :host([open='true']) {
      opacity: 1;
      visibility: visible;
      z-index: 1000;
    }

    .button {
      width: var(--hh-close-button-width, 4rem);
      position: fixed;
      top: var(--hh-close-button-top, 4rem);
      right: var(--hh-close-button-right, 4rem);
      display: flex;
      background-color: var(--hh-background);
      border: 0.1rem solid var(--hh-border-color);
      border-width: var(--hh-close-button-border-width, 0.1rem);
      border-radius: 50%;
      color: var(--hh-color);
      align-items: center;
      justify-content: center;
      cursor: pointer;
      padding: var(--hh-close-button-padding, 1.2rem);
      z-index: 2;
    }

    .button svg {
      width: 100%;
      height: auto;
    }

    :host([size='flyout']) .button {
      position: absolute;
      top: var(--hh-close-top, 10px);
      right: var(--hh-close-right, 10px);
    }

    .content {
      display: flex;
      flex-direction: column;
      height: 100vh;
      overflow: auto;
      width: 100%;
      padding: var(--hh-padding);
    }

    :host([size='flyout']) .content {
      height: auto;
    }

    ::slotted(*) {
      width: var(--hh-slotted-width, auto);
      height: var(--hh-slotted-height, auto);
      margin: var(--hh-slotted-margin);
    }

    :host([size='popup']) div[role='dialog'] {
      background-color: var(--hh-background);
      box-shadow: 0px 4px 10px rgb(63 63 68 / 40%);
      left: 50%;
      max-width: var(--hh-popup-max-width, 1120px);
      position: relative;
      top: 50%;
      transform: translate(-50%, -50%);
      width: calc(100% - 40px);
    }

    :host([size='popup']) .content {
      border: none;
      height: auto;
      padding: 0;
      width: 100%;
    }

    :host([size='popup']) .button {
      background: none;
      border: none;
      position: absolute;
      right: 10px;
      top: 10px;
    }
  `
}

customElements.define('hh-modal', HHModal)
